import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowUp, ArrowDown, Store, Search, ChevronDown,
  FileText, User, X, ChevronLeft, ChevronRight, Image as ImageIcon, Receipt,
} from "lucide-react";
import {
  listInventoryEntries, getInventoryEntry, listInventoryHistory,
} from "../../api/inventory";
import { listBranches } from "../../api/branches";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Card, Input, Select, EmptyState, Skeleton } from "../../components/ui";
import { DateRangePresets } from "../../components/Filters";
import { unwrapList, errorMessage, monthRange, formatDate } from "../../lib/records";

const API_LIMIT = 100;

/* ── helpers ────────────────────────────────────────────────────────────── */

const ENTRY_TYPE_STYLE = {
  RECEIPT:     { bg: "bg-emerald-100", text: "text-emerald-800", label: "Receipt" },
  CONSUMPTION: { bg: "bg-amber-100",   text: "text-amber-800",   label: "Used" },
  WASTAGE:     { bg: "bg-red-100",     text: "text-red-800",     label: "Wastage" },
  ADJUSTMENT:  { bg: "bg-blue-100",    text: "text-blue-800",    label: "Adjustment" },
  COUNT:       { bg: "bg-purple-100",  text: "text-purple-800",  label: "Count" },
};

/* ── Image lightbox ────────────────────────────────────────────────────────*/
function Lightbox({ images, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx ?? 0);
  const total = images.length;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIdx((i) => (i + 1) % total);
      if (e.key === "ArrowLeft")  setIdx((i) => (i - 1 + total) % total);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, total]);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90"
      onClick={onClose}
    >
      {/* Close */}
      <button
        onClick={onClose}
        className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white hover:bg-white/20 transition-colors"
      >
        <X size={20} />
      </button>

      {/* Counter */}
      <span className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-black/50 px-3 py-1 text-sm font-medium text-white">
        {idx + 1} / {total}
      </span>

      {/* Prev */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((i) => (i - 1 + total) % total); }}
          className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/25 transition-colors"
        >
          <ChevronLeft size={24} />
        </button>
      )}

      {/* Image */}
      <img
        src={images[idx]?.url}
        alt={`Image ${idx + 1}`}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[88vh] max-w-[92vw] rounded-lg object-contain shadow-2xl"
        onError={(e) => { e.currentTarget.src = ""; }}
      />

      {/* Next */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); setIdx((i) => (i + 1) % total); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white hover:bg-white/25 transition-colors"
        >
          <ChevronRight size={24} />
        </button>
      )}

      {/* Thumbnail strip */}
      {total > 1 && (
        <div
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto px-2"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-12 w-12 shrink-0 overflow-hidden rounded-md border-2 transition-all ${
                i === idx ? "border-white scale-110" : "border-transparent opacity-60 hover:opacity-90"
              }`}
            >
              <img src={img.url} alt="" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>,
    document.body
  );
}

/* ── Image grid bucket ─────────────────────────────────────────────────────*/
function ImageBucket({ title, images, accentClass, onOpen }) {
  if (!images?.length) return null;
  return (
    <div>
      <p className={`text-[10px] font-bold uppercase tracking-wider mb-2 ${accentClass}`}>
        {title} ({images.length})
      </p>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <button
            key={i}
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpen(i); }}
            className="relative h-16 w-16 sm:h-20 sm:w-20 overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100 hover:border-emerald-400 hover:scale-105 transition-all shadow-sm"
            title={`Open image ${i + 1}`}
          >
            <img
              src={img.url}
              alt={`${title} ${i + 1}`}
              className="h-full w-full object-cover"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
                e.currentTarget.nextSibling.style.display = "flex";
              }}
            />
            <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 text-gray-400">
              <ImageIcon size={20} />
            </div>
            <div className="absolute bottom-0 right-0 rounded-tl bg-black/50 px-1 py-0.5 text-[9px] font-bold text-white">
              {i + 1}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function TypeBadge({ type }) {
  const s = ENTRY_TYPE_STYLE[type] ?? { bg: "bg-gray-100", text: "text-gray-600", label: type ?? "—" };
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function DeltaChip({ delta, type }) {
  const incoming = type === "RECEIPT" || (type === "ADJUSTMENT" && delta >= 0);
  const zero = delta === 0;
  return (
    <div className="flex items-center gap-1">
      <span className={`text-2xl font-bold tabular-nums leading-none ${
        zero ? "text-gray-400" : incoming ? "text-emerald-700" : "text-red-600"
      }`}>{Math.abs(delta)}</span>
      {!zero && (
        <span className={`rounded-full p-0.5 ${incoming ? "bg-emerald-100" : "bg-red-100"}`}>
          {incoming
            ? <ArrowUp size={12} className="text-emerald-700" />
            : <ArrowDown size={12} className="text-red-600" />}
        </span>
      )}
    </div>
  );
}

/* ── Smooth accordion ─────────────────────────────────────────────────────*/
function AccordionPanel({ open, children }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    setHeight(open ? ref.current.scrollHeight : 0);
  }, [open, children]);
  return (
    <div style={{ height: `${height}px`, overflow: "hidden", transition: "height 260ms cubic-bezier(0.4,0,0.2,1)" }}>
      <div ref={ref}>{children}</div>
    </div>
  );
}

/* ── Expanded entry detail with image buckets + lightbox ─────────────────*/
function ExpandedEntryDetail({ detail, detailLoading, listEntry }) {
  const [lightbox, setLightbox] = useState(null); // { images, startIdx }

  // Show list-level images immediately — no flicker while detail loads
  const itemImages = detail?.itemImages ?? listEntry?.itemImages ?? [];
  const billImages = detail?.billImages ?? listEntry?.billImages ?? [];
  const lines = detail?.lines ?? [];

  return (
    <div className="bg-white/60 px-5 sm:px-7 pb-4 pt-3 space-y-4">

      {/* ── Item photos ── */}
      {itemImages.length > 0 && (
        <div className="rounded-xl border border-emerald-100 bg-emerald-50/30 p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
            Item photos ({itemImages.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {itemImages.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox({ images: itemImages, startIdx: i }); }}
                className="relative h-16 w-16 sm:h-20 sm:w-20 overflow-hidden rounded-lg border-2 border-emerald-200 bg-white hover:border-emerald-500 hover:scale-105 transition-all shadow-sm"
              >
                <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 text-gray-400">
                  <ImageIcon size={18} />
                </div>
                <div className="absolute bottom-0 right-0 rounded-tl bg-emerald-700/70 px-1 py-0.5 text-[9px] font-bold text-white">{i + 1}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Bill photos ── */}
      {billImages.length > 0 && (
        <div className="rounded-xl border border-blue-100 bg-blue-50/30 p-3 space-y-2">
          <p className="text-[10px] font-bold uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0" />
            Bill photos ({billImages.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {billImages.map((img, i) => (
              <button
                key={i}
                type="button"
                onClick={(e) => { e.stopPropagation(); setLightbox({ images: billImages, startIdx: i }); }}
                className="relative h-16 w-16 sm:h-20 sm:w-20 overflow-hidden rounded-lg border-2 border-blue-200 bg-white hover:border-blue-500 hover:scale-105 transition-all shadow-sm"
              >
                <img src={img.url} alt="" className="h-full w-full object-cover" loading="lazy"
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                <div className="absolute inset-0 hidden items-center justify-center bg-gray-100 text-gray-400">
                  <ImageIcon size={18} />
                </div>
                <div className="absolute bottom-0 right-0 rounded-tl bg-blue-700/70 px-1 py-0.5 text-[9px] font-bold text-white">{i + 1}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Line items ── */}
      {detailLoading && lines.length === 0 ? (
        <Skeleton className="h-20" />
      ) : lines.length > 0 ? (
        <div>
          <p className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">
            Items ({lines.length})
          </p>
          <div className="rounded-xl border border-emerald-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-emerald-100 bg-emerald-50/50">
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500">Item</th>
                  <th className="px-3 py-2 text-right text-xs font-semibold text-gray-500">Qty</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 hidden sm:table-cell">Unit</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-gray-500 hidden sm:table-cell">Before → After</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-emerald-100/50">
                {lines.map((line, li) => {
                  const qty = line.delta ?? line.quantity;
                  const incoming = line.type === "RECEIPT" || (line.type === "ADJUSTMENT" && (qty ?? 0) >= 0);
                  return (
                    <tr key={li} className="hover:bg-emerald-50/30">
                      <td className="px-3 py-2 font-medium text-gray-800">
                        {line.itemName ?? (typeof line.item === "string" ? "—" : (line.item?.name ?? "—"))}
                      </td>
                      <td className="px-3 py-2 text-right font-bold tabular-nums">
                        {qty != null ? (
                          <span className={incoming ? "text-emerald-700" : "text-red-600"}>
                            {incoming ? "+" : ""}{qty}
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-gray-500 hidden sm:table-cell">{line.unit ?? "—"}</td>
                      <td className="px-3 py-2 text-gray-500 tabular-nums hidden sm:table-cell">
                        {line.previousQuantity ?? "—"} → {line.newQuantity ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* No content */}
      {!detailLoading && itemImages.length === 0 && billImages.length === 0 && lines.length === 0 && (
        <p className="text-xs text-gray-400 py-2">No images or line items found.</p>
      )}

      {/* Lightbox */}
      {lightbox && (
        <Lightbox
          images={lightbox.images}
          startIdx={lightbox.startIdx}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}

/* ── Entry row (expandable) ───────────────────────────────────────────────*/
function EntryRow({ entry, isChain, onExpand, isOpen, detail, detailLoading }) {
  const id = entry._id ?? entry.id;
  const hasImages = (entry.itemImages?.length ?? 0) + (entry.billImages?.length ?? 0) > 0;

  return (
    <div className="border-b border-emerald-100/60 last:border-0">
      {/* header */}
      <button
        type="button"
        onClick={() => onExpand(id)}
        className="flex w-full items-start justify-between gap-3 px-4 sm:px-5 py-3.5 text-left transition-colors hover:bg-emerald-50/50 focus:outline-none"
      >
        <div className="min-w-0 flex-1 space-y-1.5">
          {/* top: entryId + type badge */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-gray-900 text-[15px]">{entry.entryId ?? "—"}</span>
            <TypeBadge type={entry.type} />
            {hasImages && (
              <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
                📷 {entry.imageCount ?? (entry.itemImages?.length ?? 0) + (entry.billImages?.length ?? 0)}
              </span>
            )}
          </div>

          {/* meta row */}
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1">
              <User size={11} className="text-gray-400" />
              {entry.byName ?? "—"}
            </span>
            <span>{formatDate(entry.date ?? entry.recordedAt)}</span>
            {isChain && entry.partner?.name && (
              <span className="rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700">
                {entry.partner.name}
              </span>
            )}
          </div>

          {/* note */}
          {entry.note && (
            <p className="text-xs italic text-gray-400">"{entry.note}"</p>
          )}
        </div>

        {/* right: lines count + bill + chevron */}
        <div className="shrink-0 flex flex-col items-end gap-1.5">
          <div className="flex items-center gap-2">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1 text-center">
              <p className="text-lg font-black tabular-nums leading-none text-emerald-700">
                {entry.lineCount ?? 0}
              </p>
              <p className="text-[9px] font-bold uppercase text-emerald-500">
                item{(entry.lineCount ?? 0) !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          {entry.billAmount != null && (
            <span className="text-xs font-semibold text-gray-700 tabular-nums">
              ₹{Number(entry.billAmount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
          {entry.billRef && (
            <span className="text-[10px] font-mono text-gray-400">{entry.billRef}</span>
          )}
          <ChevronDown
            size={15}
            className={`text-gray-400 transition-transform duration-260 ${isOpen ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {/* expanded detail */}
      <AccordionPanel open={isOpen}>
        <ExpandedEntryDetail
          detail={detail}
          detailLoading={detailLoading}
          listEntry={entry}
        />
      </AccordionPanel>
    </div>
  );
}

/* ── Single movement row (plain, no expand) ───────────────────────────────*/
function MovementRow({ mov, isChain }) {
  const unit     = mov.unit ?? mov.item?.unit;
  const delta    = mov.delta ?? mov.quantity ?? 0;
  const incoming = mov.type === "RECEIPT" || (mov.type === "ADJUSTMENT" && delta >= 0);
  const categoryName = mov.category?.name ?? mov.category ?? null;

  return (
    <div className="flex items-start justify-between gap-4 px-4 sm:px-5 py-3.5 border-b border-emerald-100/60 last:border-0 hover:bg-emerald-50/30 transition-colors">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-bold text-gray-900 text-[15px] leading-tight">
            {mov.itemName ?? mov.item?.name ?? "—"}
          </span>
          <TypeBadge type={mov.type} />
          {categoryName && (
            <span className="hidden sm:inline rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500">
              {categoryName}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="flex flex-col items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 min-w-[52px]">
              <span className="text-[9px] font-bold uppercase tracking-wider text-gray-400">Before</span>
              <span className="text-base font-bold tabular-nums text-gray-700">{mov.previousQuantity ?? "—"}</span>
            </div>
            <span className={`text-lg font-bold ${incoming ? "text-emerald-500" : "text-red-400"}`}>→</span>
            <div className={`flex flex-col items-center rounded-lg border px-3 py-1.5 min-w-[52px] ${incoming ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
              <span className={`text-[9px] font-bold uppercase tracking-wider ${incoming ? "text-emerald-500" : "text-red-400"}`}>After</span>
              <span className={`text-base font-bold tabular-nums ${incoming ? "text-emerald-700" : "text-red-600"}`}>{mov.newQuantity ?? "—"}</span>
            </div>
          </div>
          {unit && (
            <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-semibold text-gray-600">{unit}</span>
          )}
        </div>

        <p className="text-xs text-gray-400">
          {formatDate(mov.date ?? mov.recordedAt)}
          {mov.byName && ` · ${mov.byName}`}
          {isChain && mov.partner?.name && ` · ${mov.partner.name}`}
          {mov.entry?.entryId && (
            <span className="ml-1 font-mono text-[10px] bg-gray-100 rounded px-1">{mov.entry.entryId}</span>
          )}
        </p>
        {mov.note && <p className="text-xs italic text-gray-400">"{mov.note}"</p>}
      </div>

      <div className="shrink-0 flex flex-col items-end gap-1.5 pt-0.5">
        <DeltaChip delta={delta} type={mov.type} />
      </div>
    </div>
  );
}

/* ── Main page ─────────────────────────────────────────────────────────────*/
export default function InventoryEntries() {
  const { partner } = useAuth();
  const isChain = partner?.kind === "CHAIN";
  const [searchParams, setSearchParams] = useSearchParams();
  const bounds = monthRange();

  const [tab, setTab]         = useState(searchParams.get("tab") || "entries");
  const [from, setFrom]       = useState(bounds.from);
  const [to, setTo]           = useState(bounds.to);
  const [branchId, setBranchId] = useState(searchParams.get("partner") || "");
  const [search, setSearch]   = useState("");
  const [branches, setBranches] = useState([]);

  // Entries state
  const [entries, setEntries]           = useState([]);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState("");
  const [expandedId, setExpandedId]     = useState(null);
  const [expandedData, setExpandedData] = useState({});
  const [expandLoading, setExpandLoading] = useState(false);

  // Movement history state — paginated
  const [movements, setMovements]         = useState([]);
  const [movPage, setMovPage]             = useState(1);
  const [movPagination, setMovPagination] = useState(null);
  const [movLoading, setMovLoading]       = useState(false);
  const [movError, setMovError]           = useState("");

  useEffect(() => {
    if (!isChain) return;
    listBranches({ limit: 100 })
      .then((r) => setBranches(unwrapList(r, 100).items))
      .catch(() => setBranches([]));
  }, [isChain]);

  /* ── fetch entries ── */
  const fetchEntries = useCallback(async () => {
    setEntriesLoading(true);
    setEntriesError("");
    try {
      const base = {
        limit: API_LIMIT,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
      };
      const first = await listInventoryEntries({ ...base, page: 1 });
      const { items: firstItems, pagination } = unwrapList(first, API_LIMIT);
      let all = [...firstItems];
      if ((pagination.pages ?? 1) > 1) {
        const rest = await Promise.all(
          Array.from({ length: (pagination.pages ?? 1) - 1 }, (_, i) =>
            listInventoryEntries({ ...base, page: i + 2 }).then((r) => unwrapList(r, API_LIMIT).items)
          )
        );
        all = all.concat(rest.flat());
      }
      setEntries(all);
    } catch (err) {
      setEntries([]);
      setEntriesError(errorMessage(err, "Could not load entries."));
    } finally {
      setEntriesLoading(false);
    }
  }, [from, to, branchId, isChain]);

  /* ── fetch movement history (paginated, limit 10) ── */
  const fetchMovements = useCallback(async (p = 1) => {
    setMovLoading(true);
    setMovError("");
    try {
      const res = await listInventoryHistory({
        page: p,
        limit: 10,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(isChain && branchId ? { partner: branchId } : {}),
        ...(search.trim() ? { item: search.trim() } : {}),
      });
      const { items: rows, pagination: pg } = unwrapList(res, 10);
      setMovements(rows);
      setMovPagination(pg);
      setMovPage(p);
    } catch (err) {
      setMovements([]);
      setMovError(errorMessage(err, "Could not load movement history."));
    } finally {
      setMovLoading(false);
    }
  }, [from, to, branchId, isChain, search]);

  useEffect(() => {
    if (tab === "entries") fetchEntries();
    else fetchMovements(1);
  }, [tab, fetchEntries, fetchMovements]);

  useEffect(() => {
    const next = new URLSearchParams();
    next.set("tab", tab);
    if (isChain && branchId) next.set("partner", branchId);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, branchId]);

  /* ── expand entry row ── */
  async function toggleEntry(id) {
    if (expandedId === id) { setExpandedId(null); return; }
    setExpandedId(id);
    if (expandedData[id] !== undefined) return;
    setExpandLoading(true);
    try {
      const res = await getInventoryEntry(id);
      setExpandedData((p) => ({ ...p, [id]: res?.data ?? null }));
    } catch {
      setExpandedData((p) => ({ ...p, [id]: null }));
    } finally {
      setExpandLoading(false);
    }
  }

  /* ── filtered entries (client-side search) ── */
  const filteredEntries = search.trim()
    ? entries.filter((e) =>
        (e.entryId ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (e.byName ?? "").toLowerCase().includes(search.toLowerCase()) ||
        (e.note ?? "").toLowerCase().includes(search.toLowerCase())
      )
    : entries;

  const isLoading = tab === "entries" ? entriesLoading : movLoading;
  const pageError = tab === "entries" ? entriesError : movError;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-green-50">
      <div className="px-4 sm:px-6 py-4 sm:py-6 lg:px-8">
        <Link to="/inventory" className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-emerald-600 hover:text-emerald-700 hover:underline transition-colors">
          <ArrowLeft size={15} /> Back to stock
        </Link>

        <PageHeader
          title={tab === "entries" ? "Inventory entries" : "Movement history"}
          subtitle={tab === "entries"
            ? "All submissions — receipts, consumption records, adjustments."
            : "Item-level quantity changes grouped by category."}
          actions={
            isChain && (
              <div className="flex items-center gap-2">
                <Store size={14} className="shrink-0 text-gray-400" />
                <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}
                  className="sm:w-48 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm">
                  <option value="">All branches</option>
                  {branches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                </Select>
              </div>
            )
          }
        />

        {/* Tab switcher */}
        <div className="mb-4 flex gap-1 rounded-xl border border-emerald-100 bg-white/80 p-1 w-fit shadow-sm">
          {[
            { key: "entries", label: "Entries", icon: FileText },
            { key: "history", label: "Movement history", icon: ArrowUp },
          ].map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => { setTab(key); setSearch(""); }}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-1.5 text-sm font-medium transition-all ${
                tab === key ? "bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm" : "text-gray-600 hover:text-emerald-700"
              }`}>
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>

        <Card className="bg-white/80 backdrop-blur-sm border border-emerald-100 shadow-xl shadow-emerald-100/50 overflow-hidden">
          {/* Filters */}
          <div className="flex flex-col gap-3 border-b border-emerald-100/60 p-3 sm:p-4">
            <DateRangePresets from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t); }} />
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex items-center gap-2">
                <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)}
                  className="w-36 rounded border border-gray-200 px-2.5 py-2 text-sm text-gray-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
                <span className="text-sm text-gray-400">to</span>
                <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)}
                  className="w-36 rounded border border-gray-200 px-2.5 py-2 text-sm text-gray-800 outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20" />
              </div>
              <div className="relative flex-1">
                <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input placeholder={tab === "entries" ? "Search entries…" : "Search item name…"}
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-sm" />
              </div>
            </div>
          </div>

          {pageError && (
            <div className="border-b border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{pageError}</div>
          )}

          {isLoading ? (
            <div className="divide-y divide-emerald-100/60">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="px-5 py-4 space-y-2">
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </div>
                  <Skeleton className="h-3 w-48" />
                </div>
              ))}
            </div>
          ) : tab === "entries" ? (
            /* ── ENTRIES TAB ── */
            filteredEntries.length === 0 ? (
              <div className="p-10">
                <EmptyState icon={FileText} title="No entries found" description="Try a different date range or branch." />
              </div>
            ) : (
              <div>
                {filteredEntries.map((entry) => (
                  <EntryRow
                    key={entry._id ?? entry.id}
                    entry={entry}
                    isChain={isChain}
                    onExpand={toggleEntry}
                    isOpen={expandedId === (entry._id ?? entry.id)}
                    detail={expandedData[entry._id ?? entry.id]}
                    detailLoading={expandLoading}
                  />
                ))}
              </div>
            )
          ) : (
            /* ── MOVEMENT HISTORY TAB ── */
            movements.length === 0 ? (
              <div className="p-10">
                <EmptyState icon={ArrowUp} title="No movements found" description="Try a different date range or branch." />
              </div>
            ) : (
              <div>
                {movements.map((mov) => (
                  <MovementRow
                    key={mov._id ?? mov.id}
                    mov={mov}
                    isChain={isChain}
                  />
                ))}
              </div>
            )
          )}

          {/* Pagination for movement history */}
          {!isLoading && tab === "history" && movPagination && (movPagination.pages ?? 1) > 1 && (
            <div className="border-t border-emerald-100/60 px-5 py-3 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {movPagination.total} movements · page {movPage} of {movPagination.pages}
              </p>
              <div className="flex gap-2">
                <button
                  disabled={movPage <= 1}
                  onClick={() => fetchMovements(movPage - 1)}
                  className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <button
                  disabled={movPage >= (movPagination.pages ?? 1)}
                  onClick={() => fetchMovements(movPage + 1)}
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {!isLoading && (
            <div className="border-t border-emerald-100/60 px-5 py-3 text-xs text-gray-400">
              {tab === "entries"
                ? `${filteredEntries.length} entr${filteredEntries.length !== 1 ? "ies" : "y"}`
                : `${movements.length} of ${movPagination?.total ?? movements.length} movement${(movPagination?.total ?? movements.length) !== 1 ? "s" : ""}`}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
